import React from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import { useApolloClient as useApolloContextClient } from '@apollo/client';

import PhotoIdUpload from 'checkout/components/sections/user/photo-id-upload';
import useStores from 'shared/hooks/use-stores';
import { useInstoreEnrollmentAnalytics } from '../../hooks/use-instore-enrollment-analytics';
import { FieldWrapper, QuestionText } from './styles';

type PhotoUploadInputProps = {
  name: string;
  question: string;
};

// Reuses the same secure-image-upload flow (compression + uploadSecureImage mutation,
// S3 key returned) already used for ID/medical-card photo capture during checkout.
export const PhotoUploadInput = ({ name, question }: PhotoUploadInputProps): JSX.Element => {
  const { control, formState } = useFormContext();
  const hasError = !!formState.errors[name];
  const { logError } = useInstoreEnrollmentAnalytics();

  // PhotoIdUpload reads its Apollo client off the MobX store (shared/hooks/use-apollo-client),
  // which only the checkout flow populates (checkout/isolated/use-store-map.ts). Outside
  // checkout that property is never set, so mirror the same assignment here.
  const stores = useStores();
  const apolloClient = useApolloContextClient();
  stores.apolloClient = apolloClient;

  const handleUploadError = (error: unknown): void => {
    logError('Drivers license/MMJID photo upload failed', {
      field: name,
      errorMessage: error instanceof Error ? error.message : String(error),
    });
  };

  return (
    <FieldWrapper id={name}>
      <QuestionText>{question}</QuestionText>
      <Controller
        name={name}
        control={control}
        defaultValue=''
        render={({ value, onChange }) => (
          <PhotoIdUpload
            id={name}
            value={value}
            guest={false}
            medical={false}
            error={hasError}
            onPhotoUploaded={onChange}
            onUploadError={handleUploadError}
            onPhotoReset={() => onChange('')}
            onValueAfterError={() => undefined}
          />
        )}
      />
    </FieldWrapper>
  );
};
